import React, { useState, useEffect } from "react";
import Input from "../../../components/common/Input";
import TextArea from "../../../components/common/TextArea";
import Select from "../../../components/common/Select";
import Tab from "../../../components/common/Tab";
import RichTextEditor from "../../../components/common/RichTextEditor";
import { DocumentTextIcon } from "@heroicons/react/outline";

const CampaignContentStep = ({ data, templates, onChange }) => {
  const [useTemplate, setUseTemplate] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    if (data.templateKey) {
      const template = templates.find((t) => t.key === data.templateKey);
      setSelectedTemplate(template);
    }
  }, [data.templateKey, templates]);

  const handleTemplateChange = (templateKey) => {
    const template = templates.find((t) => t.key === templateKey);
    setSelectedTemplate(template);
    onChange({
      ...data,
      templateKey,
      templateVersion: template?.version,
      variables: {},
      // Clear raw fields when selecting template
      rawTitle: "",
      rawBody: "",
      rawImageUrl: "",
      rawIconUrl: "",
      rawDeeplink: "",
      rawActions: [],
    });
  };

  const handleVariableChange = (key, value) => {
    onChange({
      ...data,
      variables: {
        ...data.variables,
        [key]: value,
      },
    });
  };

  const handleRawFieldChange = (field, value) => {
    onChange({
      ...data,
      [field]: value,
      // Clear template when using raw content
      templateKey: "",
      templateVersion: null,
      variables: {},
    });
  };

  const extractVariables = (template) => {
    if (!template) return [];

    const variablePattern = /{{(\w+)}}/g;
    const variables = new Set();

    // Extract from all template fields
    const fields = [
      template.titleTpl,
      template.bodyTpl,
      template.imageUrlTpl,
      template.iconUrlTpl,
      template.deeplinkTpl,
    ];

    fields.forEach((field) => {
      if (field) {
        const matches = field.match(variablePattern);
        if (matches) {
          matches.forEach((match) => {
            variables.add(match.slice(2, -2));
          });
        }
      }
    });

    return Array.from(variables);
  };

  const tabs = [
    {
      label: "Use Template",
      content: (
        <div className="space-y-4">
          <Select
            label="Select Template"
            value={data.templateKey}
            onChange={(e) => handleTemplateChange(e.target.value)}
            required
          >
            <option value="">Choose a template...</option>
            {templates.map((template) => (
              <option key={template.key} value={template.key}>
                {template.name || template.key} (v{template.version})
              </option>
            ))}
          </Select>

          {selectedTemplate && (
            <>
              {/* Template Preview */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-sm">Template Preview</h4>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Title</p>
                  <p className="text-sm">{selectedTemplate.titleTpl}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Body</p>
                  <div
                    className="text-sm"
                    dangerouslySetInnerHTML={{
                      __html: selectedTemplate.bodyTpl,
                    }}
                  />
                </div>
                {selectedTemplate.imageUrlTpl && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Image</p>
                    <p className="text-sm font-mono">
                      {selectedTemplate.imageUrlTpl}
                    </p>
                  </div>
                )}
              </div>

              {/* Variables */}
              {extractVariables(selectedTemplate).length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-3">
                    Template Variables
                  </h4>
                  <div className="space-y-3">
                    {extractVariables(selectedTemplate).map((variable) => (
                      <Input
                        key={variable}
                        label={variable}
                        value={data.variables[variable] || ""}
                        onChange={(e) =>
                          handleVariableChange(variable, e.target.value)
                        }
                        placeholder={`Enter value for ${variable}...`}
                        required
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      label: "Custom Content",
      content: (
        <div className="space-y-4">
          <Input
            label="Title"
            value={data.rawTitle}
            onChange={(e) => handleRawFieldChange("rawTitle", e.target.value)}
            placeholder="Notification title..."
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Body
            </label>
            <RichTextEditor
              value={data.rawBody}
              onChange={(value) => handleRawFieldChange("rawBody", value)}
              placeholder="Write your notification message..."
              maxLength={1000}
            />
          </div>

          <Input
            label="Image URL (Optional)"
            value={data.rawImageUrl}
            onChange={(e) =>
              handleRawFieldChange("rawImageUrl", e.target.value)
            }
            placeholder="https://example.com/image.jpg"
          />

          <Input
            label="Icon URL (Optional)"
            value={data.rawIconUrl}
            onChange={(e) => handleRawFieldChange("rawIconUrl", e.target.value)}
            placeholder="https://example.com/icon.png"
          />

          <Input
            label="Deep Link (Optional)"
            value={data.rawDeeplink}
            onChange={(e) =>
              handleRawFieldChange("rawDeeplink", e.target.value)
            }
            placeholder="app://screen/detail"
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <Tab
        tabs={tabs}
        defaultTab={data.templateKey ? 0 : 1}
        onTabChange={(index) => setUseTemplate(index === 0)}
      />
    </div>
  );
};

export default CampaignContentStep;
